import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  Volume2,
  VolumeX,
  ChevronLeft,
  ChevronRight,
  Play,
  Square,
  Search,
  Info,
  BookOpen,
  HelpCircle,
  Compass,
  CheckCircle2,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  LayoutDashboard,
  CalendarDays,
  Activity,
  Inbox,
  Users,
  BarChart2,
  Settings,
  PlusCircle,
  Filter,
  FolderKanban,
  LayoutList,
  Layers,
  Package,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useHelpDrawer, type HelpTab } from '@/contexts/HelpDrawerContext';
import { getHelpContent, type HelpTourStep } from '@/data/helpContent';
import { useTour } from '@/contexts/TourContext';
import { useScheduler } from '@/contexts/SchedulerContext';
import type { UtilisationTier } from '@/types';
import { getEnabledTiers } from '@/utils/utilization-labels';

// ─── Icon Map ──────────────────────────────────────────────────────────────────
const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  LayoutDashboard, CalendarDays, Activity, Inbox, Users, BarChart2,
  Settings, PlusCircle, Filter, FolderKanban, Search, Compass,
  LayoutList, Layers, Package,
};

// ─── Types ─────────────────────────────────────────────────────────────────────

interface HelpDrawerProps {
  currentPage: string;
  onNavigate: (page: string) => void;
}

// ─── Voice helpers (mirrors TourContext speech logic) ─────────────────────────

function pickVoice(): SpeechSynthesisVoice | null {
  if (typeof window === 'undefined' || !window.speechSynthesis) return null;
  const voices = window.speechSynthesis.getVoices();
  if (!voices.length) return null;
  const lower = (s: string) => s.toLowerCase();
  return (
    voices.find((v) => lower(v.name).includes('google uk english female')) ??
    voices.find((v) => v.lang === 'en-GB' && lower(v.name).includes('female')) ??
    voices.find((v) => v.lang === 'en-GB') ??
    voices.find((v) => v.lang.startsWith('en') && lower(v.name).includes('female')) ??
    voices.find((v) => v.lang.startsWith('en')) ??
    null
  );
}

function speak(text: string) {
  if (typeof window === 'undefined' || !window.speechSynthesis) return;
  window.speechSynthesis.cancel();
  const utt = new SpeechSynthesisUtterance(text);
  utt.lang = 'en-GB';
  utt.rate = 0.92;
  utt.pitch = 1.1;
  const voice = pickVoice();
  if (voice) utt.voice = voice;
  setTimeout(() => window.speechSynthesis.speak(utt), 50);
}

function stopSpeech() {
  if (typeof window !== 'undefined' && window.speechSynthesis) {
    window.speechSynthesis.cancel();
  }
}

// ─── Tab Button ───────────────────────────────────────────────────────────────

function TabButton({
  id, label, icon: Icon, isActive, onClick, badge,
}: {
  id: HelpTab;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  isActive: boolean;
  onClick: () => void;
  badge?: string;
}) {
  return (
    <button
      role="tab"
      aria-selected={isActive}
      aria-controls={`help-panel-${id}`}
      onClick={onClick}
      className={cn(
        'relative flex flex-col items-center gap-1 px-2 py-2.5 text-[10px] font-semibold uppercase tracking-wider transition-colors rounded-lg flex-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isActive
          ? 'bg-primary/10 text-primary'
          : 'text-muted-foreground hover:text-foreground hover:bg-accent',
      )}
    >
      <Icon className="h-4 w-4" />
      <span>{label}</span>
      {badge && (
        <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 text-[9px] font-bold rounded-full bg-primary text-primary-foreground flex items-center justify-center">
          {badge}
        </span>
      )}
    </button>
  );
}

// ─── Heatmap tier helpers ─────────────────────────────────────────────────────

/** Returns a colour swatch + label inline element for a single tier */
function TierSwatch({ tier }: { tier: UtilisationTier }) {
  return (
    <span className="inline-flex items-center gap-1 align-middle">
      <span
        className="inline-block w-3 h-3 rounded-sm flex-shrink-0 border border-black/10"
        style={{ backgroundColor: tier.color }}
        aria-hidden="true"
      />
      <span className="font-semibold text-foreground">{tier.customLabel || tier.label}</span>
      <span className="text-muted-foreground">
        ({tier.minPercentage}–{tier.maxPercentage >= 999 ? '∞' : tier.maxPercentage}%)
      </span>
    </span>
  );
}

/** Builds the dynamic "Colour-Coded Utilisation" description from live tiers */
function buildTierFeatureDescription(tiers: UtilisationTier[]): React.ReactNode {
  const enabled = getEnabledTiers(tiers);
  if (!enabled.length) {
    return 'Cell colours map to your configured utilisation bands, so capacity issues are visible at a glance.';
  }
  return (
    <span>
      Cell colours map to your configured utilisation bands:{' '}
      {enabled.map((tier, i) => (
        <React.Fragment key={tier.id}>
          <TierSwatch tier={tier} />
          {i < enabled.length - 2 ? ', ' : i === enabled.length - 2 ? ', and ' : ''}
        </React.Fragment>
      ))}
      {' '}— so capacity issues are visible at a glance.
    </span>
  );
}

/** Builds the dynamic overview sentence that replaces hard-coded green/amber/red */
function buildHeatmapOverview(tiers: UtilisationTier[]): React.ReactNode {
  const enabled = getEnabledTiers(tiers);
  if (!enabled.length) {
    return 'The Heatmap provides a colour-coded bird\'s-eye view of how your team\'s capacity is being used. Each cell represents one resource for one time period, making bottlenecks immediately visible without drilling into individual calendars.';
  }
  const tierList = enabled.map((t, i) => (
    <React.Fragment key={t.id}>
      <TierSwatch tier={t} />
      {i < enabled.length - 2 ? ', ' : i === enabled.length - 2 ? ' through ' : ''}
    </React.Fragment>
  ));
  return (
    <span>
      The Heatmap provides a colour-coded bird&apos;s-eye view of how your team&apos;s capacity is being used.
      Each cell represents one resource for one time period. Your current bands run from{' '}
      {tierList}
      , making bottlenecks immediately visible without drilling into individual calendars.
    </span>
  );
}

/** Builds a dynamic FAQ answer for "what do the colours mean?" */
function buildHeatmapColourFaqAnswer(tiers: UtilisationTier[]): string {
  const enabled = getEnabledTiers(tiers);
  if (!enabled.length) return 'Cell colours map to your configured utilisation bands. The exact thresholds can be configured in Settings → General.';
  const parts = enabled.map(
    (t) => `${t.customLabel || t.label} (${t.minPercentage}–${t.maxPercentage >= 999 ? '∞' : t.maxPercentage}%)`
  );
  return `Your configured bands are: ${parts.join(', ')}. The colours and thresholds can be adjusted in Settings → General under Utilisation Bands.`;
}

/** Builds a dynamic user-guide step for identifying overloaded resources */
function buildOverloadedStepText(tiers: UtilisationTier[]): string {
  const enabled = getEnabledTiers(tiers);
  const lastTier = enabled[enabled.length - 1];
  const label = lastTier ? (lastTier.customLabel || lastTier.label) : 'overloaded';
  return `Look for ${label} cells in the heatmap grid — these indicate over-allocation above ${lastTier?.minPercentage ?? 100}%.`;
}

// ─── Search highlight helper ──────────────────────────────────────────────────

function escapeRegexChars(s: string): string {
  return s.replace(/[-[\]/{}()*+?.\\^$|]/g, '\\$&');
}

function Highlight({ text, query }: { text: string; query: string }) {
  if (!query.trim()) return <>{text}</>;
  const escaped = escapeRegexChars(query);
  const regex = new RegExp('(' + escaped + ')', 'gi');
  const parts = text.split(regex);
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <mark key={i} className="bg-primary/20 text-foreground rounded-sm px-0.5">{part}</mark>
        ) : (
          part
        ),
      )}
    </>
  );
}

// ─── Functionality Tab ────────────────────────────────────────────────────────

function FunctionalityTab({
  currentPage,
  utilisationTiers,
  searchQuery,
}: {
  currentPage: string;
  utilisationTiers?: UtilisationTier[];
  searchQuery?: string;
}) {
  const content = getHelpContent(currentPage);
  const { functionality } = content;
  const q = (searchQuery ?? '').trim().toLowerCase();

  const isHeatmap = currentPage === 'heatmap' && !!utilisationTiers?.length;
  const dynamicOverview = isHeatmap ? buildHeatmapOverview(utilisationTiers!) : null;

  // Filter features by query
  const filteredFeatures = q
    ? functionality.features.filter(
        (f) =>
          f.title.toLowerCase().includes(q) ||
          f.description.toLowerCase().includes(q),
      )
    : functionality.features;

  const overviewText = functionality.overview ?? '';
  const overviewMatches = !q || overviewText.toLowerCase().includes(q);
  const sectionsMatches = !q || (functionality.sectionsAndFields ?? '').toLowerCase().includes(q);
  const hasResults = !q || overviewMatches || filteredFeatures.length > 0 || sectionsMatches;

  if (!hasResults) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center gap-3">
        <Search className="h-8 w-8 text-muted-foreground/40" />
        <p className="text-sm text-muted-foreground">No results found for &ldquo;{searchQuery}&rdquo;</p>
      </div>
    );
  }

  return (
    <div className="space-y-5 pb-6">
      {/* Overview */}
      {(!q || overviewMatches) && (
        <section>
          <h3 className="text-sm font-bold text-foreground mb-2">Overview</h3>
          <p className="text-sm leading-relaxed text-muted-foreground">
            {dynamicOverview
              ? dynamicOverview
              : q
              ? <Highlight text={functionality.overview} query={searchQuery!} />
              : functionality.overview}
          </p>
        </section>
      )}

      {(!q || filteredFeatures.length > 0) && (
        <>
          {(!q || overviewMatches) && <div className="h-px bg-border" />}
          {/* Key Features */}
          <section>
            <h3 className="text-sm font-bold text-foreground mb-3">Key Features</h3>
            <ul className="space-y-3">
              {filteredFeatures.map((feature) => {
                const isDynamicTierFeature = isHeatmap && feature.title === 'Colour-Coded Utilisation';
                return (
                  <li key={feature.title} className="flex items-start gap-2.5">
                    <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                    <span className="text-sm text-muted-foreground leading-relaxed">
                      <span className="font-semibold text-foreground">
                        {q ? <Highlight text={feature.title} query={searchQuery!} /> : feature.title}
                      </span>
                      {' — '}
                      {isDynamicTierFeature
                        ? buildTierFeatureDescription(utilisationTiers!)
                        : q
                        ? <Highlight text={feature.description} query={searchQuery!} />
                        : feature.description}
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
        </>
      )}

      {functionality.sectionsAndFields && (!q || sectionsMatches) && (
        <>
          <div className="h-px bg-border" />
          <section>
            <h3 className="text-sm font-bold text-foreground mb-2">Sections &amp; Fields</h3>
            <p className="text-sm leading-relaxed text-muted-foreground">
              {q
                ? <Highlight text={functionality.sectionsAndFields} query={searchQuery!} />
                : functionality.sectionsAndFields}
            </p>
          </section>
        </>
      )}
    </div>
  );
}

// ─── User Guide Tab ───────────────────────────────────────────────────────────

function UserGuideTab({
  currentPage,
  utilisationTiers,
  searchQuery,
}: {
  currentPage: string;
  utilisationTiers?: UtilisationTier[];
  searchQuery?: string;
}) {
  const content = getHelpContent(currentPage);
  const { userGuide } = content;
  const q = (searchQuery ?? '').trim().toLowerCase();
  const isHeatmap = currentPage === 'heatmap' && !!utilisationTiers?.length;

  // When searching, auto-expand all matching sections; otherwise use accordion
  const [openSection, setOpenSection] = useState<number>(0);
  const toggle = (i: number) => setOpenSection((prev) => (prev === i ? -1 : i));

  // Filter sections (and their steps) that match the query
  const filteredSections = q
    ? userGuide.sections
        .map((section, origIndex) => {
          const titleMatch = section.title.toLowerCase().includes(q);
          const filteredSteps = section.steps.filter((s) =>
            s.text.toLowerCase().includes(q),
          );
          const noteMatch = section.note ? section.note.toLowerCase().includes(q) : false;
          if (titleMatch || filteredSteps.length > 0 || noteMatch) {
            return {
              section,
              origIndex,
              filteredSteps: titleMatch ? section.steps : filteredSteps,
            };
          }
          return null;
        })
        .filter(
          (x): x is { section: typeof userGuide.sections[0]; origIndex: number; filteredSteps: typeof userGuide.sections[0]['steps'] } =>
            x !== null,
        )
    : userGuide.sections.map((section, origIndex) => ({
        section,
        origIndex,
        filteredSteps: section.steps,
      }));

  const introMatches = !q || (userGuide.intro ?? '').toLowerCase().includes(q);

  if (q && filteredSections.length === 0 && !introMatches) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center gap-3">
        <Search className="h-8 w-8 text-muted-foreground/40" />
        <p className="text-sm text-muted-foreground">No results found for &ldquo;{searchQuery}&rdquo;</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-6">
      {/* Intro callout */}
      {userGuide.intro && (!q || introMatches) && (
        <div className="flex gap-3 p-3.5 rounded-lg bg-primary/8 border border-primary/20">
          <Info className="h-4 w-4 text-primary mt-0.5 shrink-0" />
          <p className="text-sm text-primary leading-relaxed">
            {q ? <Highlight text={userGuide.intro} query={searchQuery!} /> : userGuide.intro}
          </p>
        </div>
      )}

      {/* Collapsible sections */}
      {filteredSections.map(({ section, origIndex, filteredSteps }) => {
        const isOpen = q ? true : openSection === origIndex;
        return (
          <div
            key={origIndex}
            className={cn(
              'rounded-lg border transition-colors',
              isOpen ? 'border-primary/30 bg-card' : 'border-border bg-card',
            )}
          >
            <button
              onClick={() => { if (!q) toggle(origIndex); }}
              className={cn(
                'w-full flex items-center justify-between gap-3 px-4 py-3.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-lg',
                q && 'cursor-default',
              )}
              aria-expanded={isOpen}
            >
              <span className="text-sm font-semibold text-foreground leading-snug">
                {q ? <Highlight text={section.title} query={searchQuery!} /> : section.title}
              </span>
              {!q && (isOpen
                ? <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" />
                : <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
              )}
            </button>

            {isOpen && (
              <div className="px-4 pb-4 space-y-3">
                <ol className="space-y-2.5">
                  {filteredSteps.map((s) => {
                    // For the heatmap "identify overloaded resources" section, patch step 1
                    const stepText = (isHeatmap && origIndex === 0 && s.step === 1 && utilisationTiers)
                      ? buildOverloadedStepText(utilisationTiers)
                      : s.text;
                    return (
                      <li key={s.step} className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/15 text-primary text-[11px] font-bold flex items-center justify-center mt-0.5">
                          {s.step}
                        </span>
                        <span className="text-sm text-muted-foreground leading-relaxed">
                          {q ? <Highlight text={stepText} query={searchQuery!} /> : stepText}
                        </span>
                      </li>
                    );
                  })}
                </ol>

                {section.note && (!q || section.note.toLowerCase().includes(q)) && (
                  <div
                    className={cn(
                      'flex gap-2.5 p-3 rounded-md text-xs leading-relaxed mt-3',
                      section.noteType === 'warning'
                        ? 'bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300'
                        : 'bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-300',
                    )}
                  >
                    {section.noteType === 'warning'
                      ? <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      : <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    }
                    <span>
                      <strong>Note:</strong>{' '}
                      {q ? <Highlight text={section.note} query={searchQuery!} /> : section.note}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── FAQ Tab ──────────────────────────────────────────────────────────────────

function FAQTab({
  currentPage,
  utilisationTiers,
  searchQuery,
}: {
  currentPage: string;
  utilisationTiers?: UtilisationTier[];
  searchQuery?: string;
}) {
  const content = getHelpContent(currentPage);
  const { faq } = content;
  const q = (searchQuery ?? '').trim().toLowerCase();
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const isHeatmap = currentPage === 'heatmap' && !!utilisationTiers?.length;

  const toggle = (i: number) => setOpenIndex((prev) => (prev === i ? null : i));

  if (!faq.length) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center gap-3">
        <HelpCircle className="h-8 w-8 text-muted-foreground/40" />
        <p className="text-sm text-muted-foreground">No FAQs available for this page yet.</p>
      </div>
    );
  }

  // Filter FAQs by search query (keeping original index for dynamic answer logic)
  const filteredFaq = q
    ? faq
        .map((item, origIndex) => ({ item, origIndex }))
        .filter(
          ({ item }) =>
            item.question.toLowerCase().includes(q) ||
            item.answer.toLowerCase().includes(q),
        )
    : faq.map((item, origIndex) => ({ item, origIndex }));

  if (q && filteredFaq.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center gap-3">
        <Search className="h-8 w-8 text-muted-foreground/40" />
        <p className="text-sm text-muted-foreground">No results found for &ldquo;{searchQuery}&rdquo;</p>
      </div>
    );
  }

  return (
    <div className="space-y-2 pb-6">
      {filteredFaq.map(({ item, origIndex }) => {
        const isOpen = q ? true : openIndex === origIndex;
        const dynamicAnswer = isHeatmap && origIndex === 0 && utilisationTiers
          ? buildHeatmapColourFaqAnswer(utilisationTiers)
          : item.answer;
        return (
          <div
            key={origIndex}
            className={cn(
              'rounded-lg border transition-colors',
              isOpen ? 'border-primary/30' : 'border-border',
            )}
          >
            <button
              onClick={() => { if (!q) toggle(origIndex); }}
              className={cn(
                'w-full flex items-start justify-between gap-3 px-4 py-3.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-lg',
                q && 'cursor-default',
              )}
              aria-expanded={isOpen}
            >
              <span
                className={cn(
                  'text-sm font-medium leading-snug',
                  isOpen ? 'text-primary' : 'text-foreground',
                )}
              >
                {q ? <Highlight text={item.question} query={searchQuery!} /> : item.question}
              </span>
              {!q && (isOpen
                ? <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                : <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
              )}
            </button>

            {isOpen && (
              <div className="px-4 pb-4">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {q ? <Highlight text={dynamicAnswer} query={searchQuery!} /> : dynamicAnswer}
                </p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Tour Tab ─────────────────────────────────────────────────────────────────

interface TourTabProps {
  currentPage: string;
  voiceoverEnabled: boolean;
  onToggleVoiceover: () => void;
  onClose: () => void;
  onLaunchProductTour: () => void;
}

function TourTab({ currentPage, voiceoverEnabled, onToggleVoiceover, onClose, onLaunchProductTour }: TourTabProps) {
  const content = getHelpContent(currentPage);
  const { tourSteps } = content;
  const [activeStep, setActiveStep] = useState<number>(0);
  const [isTourRunning, setIsTourRunning] = useState(false);
  const rafRef = useRef<number>(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);

  const currentTourStep: HelpTourStep | undefined = tourSteps[activeStep];

  // Highlight target element
  useEffect(() => {
    if (!isTourRunning || !currentTourStep?.targetSelector) {
      setTargetRect(null);
      return;
    }
    const measure = () => {
      const el = document.querySelector(currentTourStep.targetSelector!) as HTMLElement | null;
      if (el) {
        setTargetRect(el.getBoundingClientRect());
      } else {
        setTargetRect(null);
      }
      rafRef.current = requestAnimationFrame(measure);
    };
    const tid = setTimeout(() => {
      rafRef.current = requestAnimationFrame(measure);
    }, 100);
    return () => {
      clearTimeout(tid);
      cancelAnimationFrame(rafRef.current);
      setTargetRect(null);
    };
  }, [isTourRunning, currentTourStep]);

  // Voiceover for page tour steps
  useEffect(() => {
    if (isTourRunning && voiceoverEnabled && currentTourStep) {
      speak(`${currentTourStep.title}. ${currentTourStep.description}`);
    }
  }, [isTourRunning, activeStep, voiceoverEnabled, currentTourStep]);

  const startPageTour = () => {
    setActiveStep(0);
    setIsTourRunning(true);
    if (voiceoverEnabled && tourSteps[0]) {
      speak(`${tourSteps[0].title}. ${tourSteps[0].description}`);
    }
  };

  const stopPageTour = () => {
    setIsTourRunning(false);
    stopSpeech();
    setTargetRect(null);
  };

  const next = () => {
    if (activeStep < tourSteps.length - 1) {
      setActiveStep((p) => p + 1);
    } else {
      stopPageTour();
    }
  };

  const prev = () => {
    if (activeStep > 0) setActiveStep((p) => p - 1);
  };

  const goTo = (i: number) => setActiveStep(i);

  // Cleanup on unmount
  useEffect(() => () => stopSpeech(), []);

  const StepIcon = currentTourStep
    ? (ICON_MAP[currentTourStep.icon] ?? Compass)
    : Compass;

  return (
    <div className="space-y-5 pb-6">
      {/* Voiceover toggle */}
      <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border border-border">
        <div>
          <p className="text-xs font-semibold text-foreground">English Voiceover</p>
          <p className="text-xs text-muted-foreground mt-0.5">Female en-GB narration during tours</p>
        </div>
        <button
          onClick={onToggleVoiceover}
          aria-pressed={voiceoverEnabled}
          aria-label={voiceoverEnabled ? 'Disable voiceover' : 'Enable voiceover'}
          className={cn(
            'flex items-center justify-center w-9 h-9 rounded-lg border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            voiceoverEnabled
              ? 'border-primary bg-primary/10 text-primary'
              : 'border-border bg-background text-muted-foreground hover:text-foreground hover:bg-accent',
          )}
        >
          {voiceoverEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
        </button>
      </div>

      {/* Page-specific tour */}
      {tourSteps.length > 0 ? (
        <section>
          <h3 className="text-sm font-bold text-foreground mb-3">
            {content.pageTitle} Tour
          </h3>

          {!isTourRunning ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground leading-relaxed">
                Take a guided tour of the key features on this page. Each step highlights a specific area and explains what it does.
              </p>

              {/* Step previews */}
              <div className="space-y-2">
                {tourSteps.map((step, i) => {
                  const Icon = ICON_MAP[step.icon] ?? Compass;
                  return (
                    <button
                      key={step.id}
                      onClick={() => { setActiveStep(i); startPageTour(); }}
                      className="w-full flex items-start gap-3 p-3 rounded-lg border border-border hover:border-primary/30 hover:bg-primary/5 text-left transition-colors group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <span className="flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-md bg-primary/10 text-primary text-xs font-bold">
                        {i + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">
                          {step.title}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                          {step.description}
                        </p>
                      </div>
                      <Icon className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5 group-hover:text-primary transition-colors" />
                    </button>
                  );
                })}
              </div>

              <Button onClick={startPageTour} className="w-full gap-2 mt-2">
                <Play className="h-4 w-4" />
                Start {content.pageTitle} Tour
              </Button>
            </div>
          ) : (
            /* Active tour card */
            <div className="rounded-xl border border-primary/30 bg-card overflow-hidden">
              <div className="h-1 bg-primary" />
              <div className="p-4 space-y-3">
                {/* Step header */}
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-lg bg-primary/10 text-primary">
                    <StepIcon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Step {activeStep + 1} of {tourSteps.length}
                    </p>
                    <p className="text-sm font-bold text-foreground leading-tight">
                      {currentTourStep?.title}
                    </p>
                  </div>
                  <button
                    onClick={stopPageTour}
                    aria-label="Stop tour"
                    className="ml-auto flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <Square className="h-3.5 w-3.5" />
                  </button>
                </div>

                <p className="text-sm text-muted-foreground leading-relaxed">
                  {currentTourStep?.description}
                </p>

                {/* Progress dots */}
                <div className="flex items-center gap-1.5">
                  {tourSteps.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => goTo(i)}
                      aria-label={`Go to step ${i + 1}`}
                      className={cn(
                        'rounded-full transition-all duration-200 focus-visible:outline-none',
                        i === activeStep ? 'w-5 h-2 bg-primary' :
                        i < activeStep   ? 'w-2 h-2 bg-primary/40' :
                                           'w-2 h-2 bg-muted-foreground/30',
                      )}
                    />
                  ))}
                </div>

                {/* Nav buttons */}
                <div className="flex items-center justify-between pt-1">
                  <Button
                    variant="outline" size="sm"
                    onClick={prev}
                    disabled={activeStep === 0}
                    className="gap-1.5"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />Back
                  </Button>
                  {activeStep === tourSteps.length - 1 ? (
                    <Button size="sm" onClick={stopPageTour}>Finish</Button>
                  ) : (
                    <Button size="sm" onClick={next} className="gap-1.5">
                      Next<ChevronRight className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </section>
      ) : (
        <div className="flex flex-col items-center justify-center py-6 text-center gap-2">
          <Compass className="h-6 w-6 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">No page-specific tour for this view.</p>
        </div>
      )}

      <div className="h-px bg-border" />

      {/* Full product tour launcher */}
      <section>
        <h3 className="text-sm font-bold text-foreground mb-2">Full Product Tour</h3>
        <p className="text-sm text-muted-foreground leading-relaxed mb-3">
          Take the complete guided tour of Scheduler covering all major features across every page.
        </p>
        <Button
          variant="outline"
          className="w-full gap-2"
          onClick={() => {
            onClose();
            onLaunchProductTour();
          }}
        >
          <Play className="h-4 w-4" />
          Launch Full Product Tour
        </Button>
      </section>

      {/* Spotlight overlay for page tour */}
      {isTourRunning && targetRect && createPortal(
        <PageTourSpotlight rect={targetRect} />,
        document.body,
      )}
    </div>
  );
}

// ─── Page Tour Spotlight (lightweight, non-blocking) ─────────────────────────

function PageTourSpotlight({ rect }: { rect: DOMRect }) {
  const pad = 8;
  const r   = 10;
  const svgW = window.innerWidth;
  const svgH = window.innerHeight;
  const rx = rect.left - pad;
  const ry = rect.top  - pad;
  const rw = rect.width  + pad * 2;
  const rh = rect.height + pad * 2;

  return (
    <svg
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: 10040, width: svgW, height: svgH }}
      aria-hidden="true"
    >
      <defs>
        <mask id="help-tour-spotlight-mask">
          <rect x={0} y={0} width={svgW} height={svgH} fill="white" />
          <rect x={rx} y={ry} width={rw} height={rh} rx={r} ry={r} fill="black" />
        </mask>
      </defs>
      <rect x={0} y={0} width={svgW} height={svgH} fill="rgba(0,0,0,0.4)" mask="url(#help-tour-spotlight-mask)" />
      <rect
        x={rx - 2} y={ry - 2} width={rw + 4} height={rh + 4}
        rx={r + 2} ry={r + 2}
        fill="none" stroke="hsl(var(--primary))" strokeWidth={2.5} opacity={0.9}
      />
    </svg>
  );
}

// ─── Related Pages Footer ─────────────────────────────────────────────────────

function RelatedPages({ currentPage, onNavigate, onClose }: {
  currentPage: string;
  onNavigate: (page: string) => void;
  onClose: () => void;
}) {
  const content = getHelpContent(currentPage);
  if (!content.relatedPages?.length) return null;

  return (
    <div className="border-t border-border pt-4 mt-2">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2.5">
        Related Pages
      </p>
      <div className="flex flex-wrap gap-2">
        {content.relatedPages.map((rp) => (
          <button
            key={rp.page}
            onClick={() => { onNavigate(rp.page); onClose(); }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border border-border hover:border-primary/40 hover:bg-primary/5 text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ArrowRight className="h-3 w-3" />
            {rp.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Search Bar ───────────────────────────────────────────────────────────────

function HelpSearch({
  value, onChange,
}: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
      <input
        type="text"
        placeholder="Search help content..."
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-9 pl-9 pr-3 text-sm bg-muted/50 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground text-foreground"
      />
    </div>
  );
}

// ─── Main HelpDrawer ──────────────────────────────────────────────────────────

export function HelpDrawer({ currentPage, onNavigate }: HelpDrawerProps) {
  const { isOpen, activeTab, closeHelp, setActiveTab } = useHelpDrawer();
  const { isActive: isTourActive, startTour, voiceoverEnabled, toggleVoiceover } = useTour();
  const { settings } = useScheduler();
  const utilisationTiers = settings.utilisationTiers;
  const [searchQuery, setSearchQuery] = useState('');

  const content = getHelpContent(currentPage);

  // Close the help drawer the moment the product tour starts so the tour
  // overlay and any drawers it opens (booking form, filters) are fully visible.
  useEffect(() => {
    if (isTourActive) closeHelp();
  }, [isTourActive, closeHelp]);

  // Reset search when tab/page changes
  useEffect(() => { setSearchQuery(''); }, [activeTab, currentPage]);

  const tabs: Array<{ id: HelpTab; label: string; icon: React.ComponentType<{ className?: string }> }> = [
    { id: 'functionality', label: 'Functionality', icon: Info },
    { id: 'user-guide',    label: 'User Guide',    icon: BookOpen },
    { id: 'faq',           label: 'FAQ',           icon: HelpCircle },
    { id: 'tour',          label: 'Tours',         icon: Compass },
  ];

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') closeHelp();
  }, [closeHelp]);

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/20 backdrop-blur-[1px]"
        style={{ zIndex: 9990 }}
        onClick={closeHelp}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Help — ${content.pageTitle}`}
        onKeyDown={handleKeyDown}
        className={cn(
          'fixed top-0 right-0 h-full w-[600px] max-w-[calc(100vw-2rem)] bg-background border-l border-border shadow-2xl flex flex-col',
          'transition-transform duration-300 ease-out',
        )}
        style={{ zIndex: 9991 }}
      >
        {/* ── Header ── */}
        <div className="flex-shrink-0 border-b border-border">
          <div className="flex items-start gap-3 px-5 py-4">
            <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 text-primary">
              <HelpCircle className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-base font-bold text-foreground leading-tight">{content.pageTitle}</h2>
              <p className="text-xs text-muted-foreground mt-0.5 leading-snug line-clamp-2">
                {content.pageDescription}
              </p>
            </div>
            <button
              onClick={closeHelp}
              aria-label="Close help panel"
              className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Search */}
          <div className="px-5 pb-3">
            <HelpSearch value={searchQuery} onChange={setSearchQuery} />
          </div>

          {/* Tabs */}
          <div className="flex items-stretch gap-1 px-3 pb-3" role="tablist" aria-label="Help sections">
            {tabs.map((tab) => (
              <TabButton
                key={tab.id}
                id={tab.id}
                label={tab.label}
                icon={tab.icon}
                isActive={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
                badge={tab.id === 'tour' && content.tourSteps.length > 0 ? String(content.tourSteps.length) : undefined}
              />
            ))}
          </div>
        </div>

        {/* ── Content ── */}
        <div
          id={`help-panel-${activeTab}`}
          className="flex-1 overflow-y-auto px-5 pt-5"
          role="tabpanel"
        >
          {activeTab === 'functionality' && (
            <FunctionalityTab
              currentPage={currentPage}
              utilisationTiers={utilisationTiers}
              searchQuery={searchQuery}
            />
          )}
          {activeTab === 'user-guide' && (
            <UserGuideTab
              currentPage={currentPage}
              utilisationTiers={utilisationTiers}
              searchQuery={searchQuery}
            />
          )}
          {activeTab === 'faq' && (
            <FAQTab
              currentPage={currentPage}
              utilisationTiers={utilisationTiers}
              searchQuery={searchQuery}
            />
          )}
          {activeTab === 'tour' && (
            <TourTab
              currentPage={currentPage}
              voiceoverEnabled={voiceoverEnabled}
              onToggleVoiceover={toggleVoiceover}
              onClose={closeHelp}
              onLaunchProductTour={startTour}
            />
          )}

          {/* Related pages */}
          {activeTab !== 'tour' && (
            <RelatedPages
              currentPage={currentPage}
              onNavigate={onNavigate}
              onClose={closeHelp}
            />
          )}
        </div>
      </div>
    </>,
    document.body,
  );
}
